import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/common/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { LucideChevronDown, LucideFileStack, LucideHeart, LucideX } from 'lucide-react';
import { cn } from '~/common/utils/styleUtil';
import { NavDropDownMenuItem } from '~/shell/components/NavDropDownMenuItem';
import { Link, NavLink } from 'react-router';
import { useBasePath } from '~/common/hooks/useBasePath';
import { Button } from '~/common/components/ui/button';
import { useScrollLock } from 'usehooks-ts';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { PgArea, TgArea } from '~/common/types/area';
import { useFavoriteTemplates } from '~/features/templates/routes/resources/FavoriteTemplatesRoute.tsx';
import { Media } from '~/media.ts';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';
import { GOOGLE_ANALYTICS_NAVIGATION_LABEL } from '~/tracking/googleAnalytics';

export interface TemplateNavigationProps {
  loginInfo?: IWebshopLoginInfoDtoData | null;
}

interface NavigationItem {
  label: string;
  link: string;
}

export function TemplateNavigation({ loginInfo }: Readonly<TemplateNavigationProps>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { path } = useBasePath();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { area } = useBasePath();
  const { templates } = useFavoriteTemplates(area === TgArea);
  const { lock, unlock } = useScrollLock({ autoLock: false });

  const isAgh = useMemo(
    () => area === PgArea && (loginInfo?.customerType === 'AGH' || loginInfo?.customerType === 'MULTICHANNEL'),
    [area, loginInfo?.customerType]
  );

  const navigationItems: NavigationItem[] = useMemo(() => {
    if (isAgh) {
      return [
        { label: t('Gekaufte Artikel'), link: `${path}/salesorderset` },
        { label: t('Einkaufsliste'), link: `${path}/shoppinglist` }
      ];
    } else {
      return [
        { label: t('Gekaufte Artikel'), link: `${path}/salesorderset` },
        { label: t('Bestellvorlagen'), link: `${path}/templates` },
        { label: t('Scan-Positionen'), link: `${path}/scanpool` },
        { label: t('Fremdartikel'), link: `${path}/externalarticles` }
      ];
    }
  }, [isAgh, path, t]);

  const handleDrawerClose = useCallback(() => {
    unlock();
    setIsDrawerOpen(false);
  }, [unlock]);

  const handleDrawerOpen = useCallback(() => {
    lock();
    setIsDrawerOpen(true);
  }, [lock]);

  if (!loginInfo) {
    return null;
  }

  return (
    <>
      <Media lessThan="desktop">
        <div>
          {isDrawerOpen ? (
            <div className={'absolute inset-0 h-[100dvh] max-h-dvh bg-white z-50 flex flex-col'}>
              <div className={'flex justify-between items-center py-2 shrink-0'}>
                <div className={'pl-4'}>
                  <p className={'text-left font-bold'}>{t('Vorlagen')}</p>
                </div>
                <Button variant={'ghost'} onClick={() => handleDrawerClose()}>
                  <LucideX />
                </Button>
              </div>
              <div className={'flex-1 overflow-y-auto text-sm pb-24'}>
                <div className={'border-t my-4'}>
                  {templates?.map(template => (
                    <TrackNavigationClick key={template.id} label={GOOGLE_ANALYTICS_NAVIGATION_LABEL.FavoriteTemplate}>
                      <NavLink
                        onClick={() => handleDrawerClose()}
                        to={`${path}/${area === TgArea ? 'templates' : 'shoppinglist'}/${template.id}`}
                        className={({ isActive }) =>
                          cn('flex items-center p-3 w-full border-b hover:text-primary', isActive && 'text-primary')
                        }
                        data-testid={`template-navigation-${template.name}`}
                      >
                        <LucideHeart size={14} className={'mr-2 text-primary fill-primary'} />
                        {template.name}
                      </NavLink>
                    </TrackNavigationClick>
                  ))}
                  {navigationItems.map(item => (
                    <TrackNavigationClick key={item.link} label={item.label}>
                      <NavLink
                        onClick={() => handleDrawerClose()}
                        to={item.link}
                        className={({ isActive }) =>
                          cn('block p-3 w-full border-b hover:text-primary', isActive && 'text-primary')
                        }
                        data-testid={`template-navigation-${item.label}`}
                      >
                        {item.label}
                      </NavLink>
                    </TrackNavigationClick>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Button variant={'ghost'} className={'px-2'} onClick={() => handleDrawerOpen()}>
              <LucideFileStack />
            </Button>
          )}
        </div>
      </Media>
      <Media greaterThanOrEqual="desktop">
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger data-testid={'template-navigation-dropdown'}>
            <div
              className={cn(
                'cursor-pointer flex items-center gap-1 px-2 hover:text-primary h-10',
                open && 'text-primary'
              )}
            >
              <LucideFileStack className={'block xl:hidden'} />
              <div className={'xl:block hidden'}>{t('Vorlagen')}</div>
              <div>
                <LucideChevronDown size={14} className={cn(open && 'rotate-180')} />
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={'p-4 max-h-[calc(100dvh-150px)] overflow-y-auto'}>
            {templates?.map(template => (
              <TrackNavigationClick key={template.id} label={GOOGLE_ANALYTICS_NAVIGATION_LABEL.FavoriteTemplate}>
                <Link
                  to={`${path}/${area === TgArea ? 'templates' : 'shoppinglist'}/${template.id}`}
                  data-testid={`template-navigation-${template.name}`}
                >
                  <NavDropDownMenuItem>
                    <LucideHeart size={16} className={'mr-2 text-primary fill-primary'} />
                    {template.name}
                  </NavDropDownMenuItem>
                </Link>
              </TrackNavigationClick>
            ))}
            {navigationItems.map((item, index) => (
              <TrackNavigationClick key={item.link} label={item.label}>
                <Link to={item.link}>
                  <NavDropDownMenuItem
                    className={cn(index === navigationItems.length - 1 && 'border-none')}
                    data-testid={`template-navigation-${item.label}`}
                  >
                    {item.label}
                  </NavDropDownMenuItem>
                </Link>
              </TrackNavigationClick>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Media>
    </>
  );
}
